import { Box, BoxProps, styled } from '@mui/material';
import {
  MobileSearchButton,
  MobileSearchModal,
  PaginatedTable,
  PaginatedTableProps,
  RowDataBase,
  TableNoDataPlaceholder,
  TableSearchBar,
} from 'common-client';
import React, { useMemo, useState } from 'react';

export interface AuctionTableProps<RowData extends RowDataBase> extends Omit<PaginatedTableProps<RowData>, 'title'> {
  /**
   * The current search text
   */
  searchText: string;
  /**
   * Callback to update the search text
   * @param searchTextNew
   */
  setSearchText: (searchTextNew: string) => void;
  /**
   * Whether auction creation is disabled
   */
  createAuctionDisabled: boolean;
  /**
   * Callback to initiate create auction flow
   */
  onCreateAuction?: () => void;
}

const StyledBox = styled(Box, { name: 'SelectedTokenBox' })<BoxProps>(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[12],
  borderRadius: '16px',
  padding: '32px',
  width: '100%',
}));

export function AuctionTable<RowData extends RowDataBase>({
  columns,
  rowData,
  onRowSelect,
  filters,
  currentFilter,
  onFilterSelect,
  sortColumnId,
  sortAscending,
  sortLabels,
  onSortChange,
  currentPageIndex,
  totalPageCount,
  onPageChange,
  searchText,
  setSearchText,
  createAuctionDisabled,
  onCreateAuction,
}: AuctionTableProps<RowData>) {
  const [searchModalOpen, setSearchModalOpen] = useState<boolean>(false);
  const searchComponent = (
    <TableSearchBar
      text={searchText}
      placeholder="Search name or paste address..."
      onUpdateText={setSearchText}
    />
  );
  const searchComponentMobile = (
    <MobileSearchButton
      isSearchActive={searchText === ''}
      onClick={() => { setSearchModalOpen(true); }}
    />
  );

  const noRowsPlaceholder = useMemo(() => {
    let title: string;
    let description: string;
    switch (currentFilter) {
      case 'All':
        title = 'There are no active auctions';
        description = 'You can create an auction with your desired assets.';
        break;
      case 'My Assets':
        title = 'There are no auctions with any of your assets';
        description = 'Ensure you connected with your desired wallet or create an auction.';
        break;
      case 'My Auctions':
        title = 'You are not participating in any auctions';
        description = 'Ensure you connected with your desired wallet. You can also join or create an auction.';
        break;
      default:
        title = 'No auctions found';
        description = 'You can create an auction with your desired assets.';
    }
    const primaryButtonProps = createAuctionDisabled ? {
      label: 'Create Auction',
      tooltip: 'User created auctions are coming soon!',
      disabled: true,
    } : {
      label: 'Create Auction',
      onClick: onCreateAuction,
    };
    return (
      <TableNoDataPlaceholder
        title={title}
        description={description}
        primaryButtonProps={primaryButtonProps}
      />
    );
  }, [currentFilter, onCreateAuction, createAuctionDisabled]);

  return (
    <>
      <StyledBox>
        <PaginatedTable
          title="Auctions"
          columns={columns}
          rowData={rowData}
          onRowSelect={onRowSelect}
          filters={filters}
          currentFilter={currentFilter}
          onFilterSelect={onFilterSelect}
          sortColumnId={sortColumnId}
          sortAscending={sortAscending}
          sortLabels={sortLabels}
          onSortChange={onSortChange}
          searchComponent={searchComponent}
          searchComponentMobile={searchComponentMobile}
          currentPageIndex={currentPageIndex}
          totalPageCount={totalPageCount}
          onPageChange={onPageChange}
          noRowsPlaceholder={noRowsPlaceholder}
        />
      </StyledBox>
      <MobileSearchModal
        open={searchModalOpen}
        title="Search Auctions"
        searchText={searchText}
        placeholder="Search name..."
        onUpdateText={setSearchText}
        onExit={() => { setSearchModalOpen(false); }}
      />
    </>
  );
}
